import { ExpensableGroupsDataCollectionQuery } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { getStatusColor } from 'modules/expenses/lib/expensables/statusColor'
import { getStatusLabel } from 'modules/expenses/lib/expensables/statusLabel'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

type ExpensableGroup =
  ExpensableGroupsDataCollectionQuery['expenses']['expensableGroupsConnection']['nodes'][number]

interface Props {
  isMyExpenses?: boolean
  locale: string
}

export const useGroupsDataCollection = ({ isMyExpenses = false, locale }: Props) => {
  return {
    sortings: {
      reviewRequestAt: {
        label: i18n.t('expenses_v2.groups_list.header.review_request_at'),
      },
      status: {
        label: i18n.t('expenses_v2.groups_list.header.status'),
      },
      amount: {
        label: i18n.t('expenses_v2.groups_list.header.amount'),
      },
    },
    defaultSorting: {
      field: 'reviewRequestAt' as const,
      order: 'desc' as const,
    },
    columns: [
      {
        label: i18n.t('expenses_v2.groups_list.header.name'),
        render: (item: ExpensableGroup) => ({
          type: 'text' as const,
          value: item.name,
        }),
      },
      ...(isMyExpenses
        ? []
        : [
            {
              label: i18n.t('expenses_v2.list.header.owner'),
              render: (item: ExpensableGroup) => ({
                type: 'person' as const,
                value: item.owner?.access
                  ? {
                      firstName: item.owner.access.fullName ?? '',
                      lastName: '',
                      src: item.owner.access.avatar
                        ? buildUrl(item.owner.access.avatar)
                        : undefined,
                      deactivated: item.owner.active === false,
                    }
                  : undefined,
              }),
            },
          ]),
      {
        label: i18n.t('expenses_v2.groups_list.header.status'),
        sorting: 'status' as const,
        render: (item: ExpensableGroup) => ({
          type: 'status' as const,
          value: {
            label: getStatusLabel(item.status),
            status: getStatusColor(item.status),
          },
        }),
      },
      {
        label: i18n.t('expenses_v2.groups_list.header.expensables_count'),
        render: (item: ExpensableGroup) => ({
          type: 'icon' as const,
          value: {
            icon: null,
            label: item.expensablesCount?.toString() || '0',
          },
        }),
      },
      {
        label: i18n.t('expenses_v2.groups_list.header.amount'),
        sorting: 'amount' as const,
        render: (item: ExpensableGroup) => ({
          type: 'text' as const,
          value: item.currency
            ? amountWithCurrency(Number(item.amount), item.currency, locale)
            : '',
        }),
      },
      {
        label: i18n.t('expenses_v2.list.header.reimbursable_amount'),
        render: (item: ExpensableGroup) => ({
          type: 'text' as const,
          value:
            item.reimbursableCurrency && item.reimbursableAmount
              ? amountWithCurrency(
                  Number(item.reimbursableAmount),
                  item.reimbursableCurrency,
                  locale
                )
              : '',
        }),
      },
      {
        label: i18n.t('expenses_v2.groups_list.header.review_request_at'),
        sorting: 'reviewRequestAt' as const,
        render: (item: ExpensableGroup) => ({
          type: 'date' as const,
          value: item.reviewRequestAt ? dateBackendDateTimeToUI(item.reviewRequestAt) : undefined,
        }),
      },
    ],
  }
}
